import Swal from 'sweetalert2';
import triggerChatFlow from '~src/utils/triggerChatFlow';
import { GLEAP_PERSONALIZATION_CATALOG_EVENT } from '~src/tracking/giottoPersonalizations';
import {
  createGiottoPersonalization,
  GiottoPersonalizationClientError,
  GIOTTO_PERSONALIZATION_ALLOWED_EXTENSIONS,
  GIOTTO_PERSONALIZATION_DESCRIPTION_MAX_LENGTH,
  GIOTTO_PERSONALIZATION_MAX_FILES,
  GIOTTO_PERSONALIZATION_MAX_FILE_SIZE,
  GIOTTO_PERSONALIZATION_NAME_MAX_LENGTH,
  GIOTTO_PERSONALIZATION_TITLE_MAX_LENGTH,
  hasAllowedGiottoPersonalizationExtension,
} from '~src/tracking/giottoPersonalizationClient';
import { fetchGiottoPersonalizationReferences } from '~src/tracking/giottoPersonalizationReferences';

const GIOTTO_PERSONALIZATION_TYPES = {
  project_type: {
    description:
      'Richiedi un nuovo tipo di progetto o la modifica di uno esistente.',
    icon: 'project_type',
    label: 'Tipo di progetto',
    namePlaceholder: 'Es. Manifesto anniversario',
    value: 'tipo_progetto',
  },
  format: {
    description: 'Personalizza misure, orientamento e impostazioni di stampa.',
    icon: 'format',
    label: 'Formato',
    namePlaceholder: 'Es. A3 orizzontale personalizzato',
    value: 'formato',
  },
  template: {
    description:
      'Richiedi un nuovo modello grafico o la modifica di uno esistente.',
    icon: 'template',
    label: 'Modello',
    namePlaceholder: 'Es. Floreale blu',
    value: 'modello',
  },
};

const GIOTTO_PERSONALIZATION_REFERENCE_RENDER_LIMIT = 60;
const GIOTTO_PERSONALIZATION_ORIENTATION_LABELS = {
  horizontal: 'Orizzontale',
  vertical: 'Verticale',
};

const GIOTTO_PERSONALIZATION_ACTIONS = {
  create: {
    description: 'Parti da zero con una nuova personalizzazione.',
    icon: 'create',
    label: 'Nuovo',
  },
  update: {
    description: 'Richiedi una modifica a una personalizzazione esistente.',
    icon: 'update',
    label: 'Modifica',
  },
};

const getPersonalizationActions = (type, customProjectReferences = null) => {
  if (type === 'format') {
    return { create: GIOTTO_PERSONALIZATION_ACTIONS.create };
  }
  if (type === 'project_type' && customProjectReferences?.length === 0) {
    return { create: GIOTTO_PERSONALIZATION_ACTIONS.create };
  }
  return GIOTTO_PERSONALIZATION_ACTIONS;
};

const PERSONALIZATION_ICONS = {
  support: `
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M4 13v-2a8 8 0 0 1 16 0v2" />
      <path d="M4 13a2 2 0 0 1 2-2h1v6H6a2 2 0 0 1-2-2v-2Z" />
      <path d="M20 13a2 2 0 0 0-2-2h-1v6h1a2 2 0 0 0 2-2v-2Z" />
      <path d="M17 18c-1 1.3-2.7 2-5 2" />
    </svg>`,
  project_type: `
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <rect x="3" y="4" width="8" height="7" rx="1.5" />
      <rect x="13" y="4" width="8" height="7" rx="1.5" />
      <rect x="3" y="13" width="8" height="7" rx="1.5" />
      <rect x="13" y="13" width="8" height="7" rx="1.5" />
    </svg>`,
  format: `
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M8 3H5a2 2 0 0 0-2 2v3M16 3h3a2 2 0 0 1 2 2v3M21 16v3a2 2 0 0 1-2 2h-3M8 21H5a2 2 0 0 1-2-2v-3" />
      <rect x="7" y="7" width="10" height="10" rx="1.5" />
    </svg>`,
  event: `
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M7 3v4M17 3v4M3 10h18" />
      <path d="m9 15 2 2 4-4" />
    </svg>`,
  template: `
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M6 3h8l4 4v14H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Z" />
      <path d="M14 3v5h5M8 12h6M8 16h7" />
    </svg>`,
  create: `
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M12 5v14M5 12h14" />
    </svg>`,
  update: `
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="m14 5 5 5M4 20l3.5-.7L19 7.8a2.1 2.1 0 0 0-3-3L4.7 16.3 4 20Z" />
    </svg>`,
  arrow: `
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="m9 18 6-6-6-6" />
    </svg>`,
  upload: `
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M12 16V4M7 9l5-5 5 5M5 20h14" />
    </svg>`,
  paperclip: `
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="m20 12-8.5 8.5a5 5 0 0 1-7-7L14 4a3.5 3.5 0 0 1 5 5l-9.5 9.5a2 2 0 0 1-3-3L15 7" />
    </svg>`,
  back: `
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="m15 18-6-6 6-6" />
    </svg>`,
};

const renderPersonalizationHeader = (title, subtitle) => `
  <header class="giotto-personalization-modal__header">
    <span class="giotto-personalization-modal__support-icon">
      ${PERSONALIZATION_ICONS.support}
    </span>
    <span>
      <span class="giotto-personalization-modal__title">${title}</span>
      <span class="giotto-personalization-modal__subtitle">${subtitle}</span>
    </span>
  </header>`;

const renderPersonalizationChoice = ([key, choice]) => `
  <button
    type="button"
    class="giotto-personalization-choice"
    data-giotto-personalization-choice="${key}"
  >
    <span class="giotto-personalization-choice__icon">
      ${PERSONALIZATION_ICONS[choice.icon]}
    </span>
    <span class="giotto-personalization-choice__copy">
      <strong>${choice.label}</strong>
      <span>${choice.description}</span>
    </span>
    <span class="giotto-personalization-choice__arrow">
      ${PERSONALIZATION_ICONS.arrow}
    </span>
  </button>`;

const showPersonalizationChoiceDialog = async ({
  choices,
  showBackButton = false,
  subtitle,
  title,
}) => {
  let selectedChoice = null;
  const result = await Swal.fire({
    ariaLabel: title,
    html: `
      ${renderPersonalizationHeader(title, subtitle)}
      <div class="giotto-personalization-modal__grid">
        ${Object.entries(choices).map(renderPersonalizationChoice).join('')}
      </div>`,
    showCloseButton: true,
    showConfirmButton: false,
    showCancelButton: showBackButton,
    cancelButtonText: 'Torna alle tipologie',
    customClass: {
      popup: 'giotto-personalization-modal',
      htmlContainer: 'giotto-personalization-modal__content',
      closeButton: 'giotto-personalization-modal__close',
      actions: 'giotto-personalization-modal__actions',
      cancelButton: 'giotto-personalization-modal__back',
    },
    didOpen: (popup) => {
      popup
        .querySelectorAll('[data-giotto-personalization-choice]')
        .forEach((button) => {
          button.addEventListener('click', () => {
            selectedChoice = button.dataset.giottoPersonalizationChoice;
            Swal.close();
          });
        });
    },
  });

  return {
    dismiss: result.dismiss,
    value: selectedChoice,
  };
};

const renderPersonalizationAction = ([key, action]) => `
  <button
    type="button"
    role="radio"
    aria-checked="false"
    class="giotto-personalization-action"
    data-giotto-personalization-action="${key}"
  >
    <span class="giotto-personalization-choice__icon">
      ${PERSONALIZATION_ICONS[action.icon]}
    </span>
    <span class="giotto-personalization-choice__copy">
      <strong>${action.label}</strong>
      <span>${action.description}</span>
    </span>
  </button>`;

const getPersonalizationTicketTitle = (type, action) =>
  `${action === 'create' ? 'Nuovo' : 'Modifica'} ${GIOTTO_PERSONALIZATION_TYPES[
    type
  ].label.toLowerCase()}`;

const getPersonalizationReferenceTitle = (type, referenceName) =>
  `Modifica ${GIOTTO_PERSONALIZATION_TYPES[
    type
  ].label.toLowerCase()} “${referenceName}”`.slice(
    0,
    GIOTTO_PERSONALIZATION_TITLE_MAX_LENGTH
  );

const getPersonalizationReferencePayload = (reference) => ({
  className: reference.className,
  id: reference.id,
  isGlobal: reference.isGlobal,
  name: reference.name,
});

const getNewPersonalizationTitle = ({
  orientation,
  projectTypeName,
  requestedName,
  type,
}) => {
  const baseTitle = getPersonalizationTicketTitle(type, 'create');
  const namedTitle = requestedName
    ? `${baseTitle}: ${requestedName}`
    : baseTitle;
  const orientationLabel =
    type === 'format'
      ? GIOTTO_PERSONALIZATION_ORIENTATION_LABELS[orientation]
      : null;
  const composedTitle = orientationLabel
    ? `${namedTitle} — ${orientationLabel}`
    : namedTitle;
  const title =
    type === 'format' && projectTypeName
      ? `${composedTitle} per “${projectTypeName}”`
      : composedTitle;
  return title.slice(0, GIOTTO_PERSONALIZATION_TITLE_MAX_LENGTH);
};

const getPersonalizationDescriptionPlaceholder = (type, action) => {
  const label = GIOTTO_PERSONALIZATION_TYPES[type].label.toLowerCase();
  return action === 'update'
    ? `Descrivi cosa vuoi modificare nel ${label} esistente e il risultato desiderato`
    : `Descrivi il nuovo ${label} che vuoi ottenere, includendo esempi e requisiti utili`;
};

const formatFileSize = (size) => {
  if (size < 1024 * 1024) return `${Math.ceil(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const getPersonalizationRequestError = (error) => {
  if (error instanceof GiottoPersonalizationClientError) {
    if (error.code === 'FORBIDDEN') {
      return 'Solo gli amministratori possono inviare questa richiesta.';
    }
    if (error.code === 'INVALID_REQUEST') {
      return 'I dati della richiesta non sono validi. Controllali e riprova.';
    }
    if (error.code === 'INVALID_REFERENCE') {
      return 'L’elemento da modificare non è più disponibile. Selezionane un altro.';
    }
    if (error.code === 'REFERENCE_UNAVAILABLE') {
      return 'Non è stato possibile verificare l’elemento da modificare. Riprova.';
    }
    if (error.code === 'GLEAP_NOT_CONFIGURED') {
      return 'Il servizio richieste non è ancora configurato.';
    }
  }
  return 'Non è stato possibile inviare la richiesta. Riprova.';
};

const showGiottoPersonalizationRequestForm = async ({
  initialAction = null,
  showBackButton = false,
  type,
}) => {
  const personalizationType = GIOTTO_PERSONALIZATION_TYPES[type];
  if (!personalizationType) return 'cancelled';

  let customProjectReferences = null;
  if (type === 'project_type') {
    try {
      customProjectReferences = (
        await fetchGiottoPersonalizationReferences('project_type')
      ).filter((reference) => !reference.isGlobal);
    } catch (error) {
      customProjectReferences = [];
    }
  }
  const personalizationActions = getPersonalizationActions(
    type,
    customProjectReferences
  );
  if (initialAction && !personalizationActions[initialAction]) {
    return 'cancelled';
  }
  const selectedInitialAction =
    initialAction || (type === 'format' ? 'create' : null);

  let popupElement = null;
  let selectedAction = selectedInitialAction;
  let selectedFiles = [];
  let requestedName = '';
  let selectedOrientation = '';
  let selectedReference = null;
  let selectedProjectReference = null;
  let references = customProjectReferences || [];
  let referenceSearchQuery = '';
  let projectReferences = [];
  let projectReferencesLoaded = false;
  let projectReferencesLoading = false;
  let loadedReferenceKey = customProjectReferences ? type : null;
  let activeReferenceRequestKey = null;
  let automaticTitle = selectedInitialAction
    ? getPersonalizationTicketTitle(type, selectedInitialAction)
    : '';
  const personalizationNameField = `
    <div class="giotto-personalization-field" data-personalization-name-field hidden>
      <span class="giotto-personalization-label-row">
        <label class="giotto-personalization-label" for="giotto-personalization-name">
          Nome del nuovo ${personalizationType.label.toLowerCase()}
        </label>
        <span class="giotto-personalization-counter" data-personalization-name-counter>0/${GIOTTO_PERSONALIZATION_NAME_MAX_LENGTH}</span>
      </span>
      <input
        id="giotto-personalization-name"
        class="giotto-personalization-input"
        type="text"
        maxlength="${GIOTTO_PERSONALIZATION_NAME_MAX_LENGTH}"
        placeholder="${personalizationType.namePlaceholder}"
        disabled
      />
      <p class="giotto-personalization-error" data-personalization-error="name" hidden></p>
    </div>
  `;

  const result = await Swal.fire({
    ariaLabel: 'Richiedi una personalizzazione Giotto',
    html: `
      ${renderPersonalizationHeader(
        'Richiedi una personalizzazione Giotto',
        'Descrivi al Supporto il risultato che vuoi ottenere.'
      )}
      <div class="giotto-personalization-form">
        <div class="giotto-personalization-field">
          <span class="giotto-personalization-label">Tipo di richiesta</span>
          <div class="giotto-personalization-form__actions${
            type === 'format'
              ? ' giotto-personalization-form__actions--single'
              : ''
          }" role="radiogroup" aria-label="Tipo di richiesta">
            ${Object.entries(personalizationActions)
              .map(renderPersonalizationAction)
              .join('')}
          </div>
          <p class="giotto-personalization-error" data-personalization-error="action" hidden></p>
        </div>

        <div class="giotto-personalization-summary" data-personalization-summary hidden>
          <span>Oggetto del ticket</span>
          <strong data-personalization-summary-value></strong>
        </div>

        ${type === 'format' ? '' : personalizationNameField}

        <div class="giotto-personalization-field" data-personalization-project-field hidden>
          <label class="giotto-personalization-label" for="giotto-personalization-project">
            Tipo di progetto
          </label>
          <select
            id="giotto-personalization-project"
            class="giotto-personalization-input giotto-personalization-select"
            hidden
            aria-hidden="true"
            disabled
          >
            <option value="">Seleziona tipo di progetto</option>
          </select>
          <div
            class="giotto-personalization-reference-cards"
            data-personalization-project-cards
            role="radiogroup"
            aria-label="Tipo di progetto"
          ></div>
          <p class="giotto-personalization-reference-status" data-personalization-project-status hidden></p>
          <p class="giotto-personalization-error" data-personalization-error="project" hidden></p>
        </div>

        <div class="giotto-personalization-field" data-personalization-orientation-field hidden>
          <label class="giotto-personalization-label" for="giotto-personalization-orientation">
            Orientamento
          </label>
          <select
            id="giotto-personalization-orientation"
            class="giotto-personalization-input giotto-personalization-select"
            disabled
          >
            <option value="">Seleziona orientamento</option>
            <option value="vertical">Verticale</option>
            <option value="horizontal">Orizzontale</option>
          </select>
          <p class="giotto-personalization-error" data-personalization-error="orientation" hidden></p>
        </div>

        ${type === 'format' ? personalizationNameField : ''}

        <div class="giotto-personalization-field" data-personalization-reference-field hidden>
          <label class="giotto-personalization-label" for="giotto-personalization-reference">
            ${personalizationType.label} da modificare
          </label>
          <select
            id="giotto-personalization-reference"
            class="giotto-personalization-input giotto-personalization-select"
            ${type === 'project_type' ? '' : 'hidden aria-hidden="true"'}
            disabled
          >
            <option value="">Seleziona ${personalizationType.label.toLowerCase()}</option>
          </select>
          <input
            class="giotto-personalization-input giotto-personalization-reference-search"
            type="search"
            data-personalization-reference-search
            aria-label="Cerca modello da modificare"
            placeholder="Cerca per nome, progetto o evento"
            ${type === 'template' ? '' : 'hidden'}
            disabled
          />
          <div
            class="giotto-personalization-reference-cards"
            data-personalization-reference-cards
            role="radiogroup"
            aria-label="${personalizationType.label} da modificare"
            ${type === 'template' ? '' : 'hidden'}
          ></div>
          <p class="giotto-personalization-reference-status" data-personalization-reference-status hidden></p>
          <p class="giotto-personalization-error" data-personalization-error="reference" hidden></p>
        </div>

        <div class="giotto-personalization-field">
          <span class="giotto-personalization-label-row">
            <label class="giotto-personalization-label" for="giotto-personalization-title">Titolo del ticket</label>
            <span class="giotto-personalization-counter" data-personalization-title-counter>0/${GIOTTO_PERSONALIZATION_TITLE_MAX_LENGTH}</span>
          </span>
          <input
            id="giotto-personalization-title"
            class="giotto-personalization-input"
            type="text"
            maxlength="${GIOTTO_PERSONALIZATION_TITLE_MAX_LENGTH}"
            placeholder="Inserisci un titolo breve e riconoscibile"
            disabled
          />
          <p class="giotto-personalization-error" data-personalization-error="title" hidden></p>
        </div>

        <div class="giotto-personalization-field">
          <span class="giotto-personalization-label-row">
            <label class="giotto-personalization-label" for="giotto-personalization-description">Descrizione</label>
            <span class="giotto-personalization-counter" data-personalization-description-counter>0/${GIOTTO_PERSONALIZATION_DESCRIPTION_MAX_LENGTH}</span>
          </span>
          <textarea
            id="giotto-personalization-description"
            class="giotto-personalization-input giotto-personalization-textarea"
            maxlength="${GIOTTO_PERSONALIZATION_DESCRIPTION_MAX_LENGTH}"
            rows="5"
            disabled
          ></textarea>
          <p class="giotto-personalization-error" data-personalization-error="description" hidden></p>
        </div>

        <div class="giotto-personalization-field">
          <span class="giotto-personalization-label">Allegati facoltativi</span>
          <label class="giotto-personalization-upload" for="giotto-personalization-files">
            <span class="giotto-personalization-upload__icon">${
              PERSONALIZATION_ICONS.upload
            }</span>
            <strong>Aggiungi file</strong>
            <span>Fino a ${GIOTTO_PERSONALIZATION_MAX_FILES} allegati · massimo 15 MB ciascuno</span>
          </label>
          <input
            id="giotto-personalization-files"
            class="giotto-personalization-file-input"
            type="file"
            accept="${GIOTTO_PERSONALIZATION_ALLOWED_EXTENSIONS.join(',')}"
            multiple
            disabled
          />
          <p class="giotto-personalization-help">Un esempio del risultato desiderato aiuta il Supporto a completare prima la richiesta.</p>
          <p class="giotto-personalization-error" data-personalization-error="files" hidden></p>
          <ul class="giotto-personalization-files" data-personalization-files></ul>
        </div>
      </div>`,
    showCloseButton: true,
    showConfirmButton: true,
    showCancelButton: true,
    showDenyButton: showBackButton,
    confirmButtonText: 'Invia richiesta',
    cancelButtonText: 'Annulla',
    denyButtonText: `${PERSONALIZATION_ICONS.back}<span>Torna alle tipologie</span>`,
    buttonsStyling: false,
    showLoaderOnConfirm: true,
    allowOutsideClick: () => !Swal.isLoading(),
    allowEscapeKey: () => !Swal.isLoading(),
    customClass: {
      popup: 'giotto-personalization-modal giotto-personalization-modal--form',
      htmlContainer: 'giotto-personalization-modal__content',
      closeButton: 'giotto-personalization-modal__close',
      actions:
        'giotto-personalization-modal__actions giotto-personalization-modal__form-footer',
      confirmButton: 'giotto-personalization-modal__submit',
      cancelButton: 'giotto-personalization-modal__cancel',
      denyButton: 'giotto-personalization-modal__back',
      validationMessage: 'giotto-personalization-modal__validation',
    },
    didOpen: (popup) => {
      popupElement = popup;
      const titleInput = popup.querySelector('#giotto-personalization-title');
      const descriptionInput = popup.querySelector(
        '#giotto-personalization-description'
      );
      const fileInput = popup.querySelector('#giotto-personalization-files');
      const nameField = popup.querySelector(
        '[data-personalization-name-field]'
      );
      const nameInput = popup.querySelector('#giotto-personalization-name');
      const nameCounter = popup.querySelector(
        '[data-personalization-name-counter]'
      );
      const projectField = popup.querySelector(
        '[data-personalization-project-field]'
      );
      const projectSelect = popup.querySelector(
        '#giotto-personalization-project'
      );
      const projectCards = popup.querySelector(
        '[data-personalization-project-cards]'
      );
      const projectStatus = popup.querySelector(
        '[data-personalization-project-status]'
      );
      const orientationField = popup.querySelector(
        '[data-personalization-orientation-field]'
      );
      const orientationSelect = popup.querySelector(
        '#giotto-personalization-orientation'
      );
      const referenceField = popup.querySelector(
        '[data-personalization-reference-field]'
      );
      const referenceSelect = popup.querySelector(
        '#giotto-personalization-reference'
      );
      const referenceCards = popup.querySelector(
        '[data-personalization-reference-cards]'
      );
      const referenceSearch = popup.querySelector(
        '[data-personalization-reference-search]'
      );
      const referenceStatus = popup.querySelector(
        '[data-personalization-reference-status]'
      );
      const uploadLabel = popup.querySelector('.giotto-personalization-upload');
      const summary = popup.querySelector('[data-personalization-summary]');
      const summaryValue = popup.querySelector(
        '[data-personalization-summary-value]'
      );
      const titleCounter = popup.querySelector(
        '[data-personalization-title-counter]'
      );
      const descriptionCounter = popup.querySelector(
        '[data-personalization-description-counter]'
      );
      const filesList = popup.querySelector('[data-personalization-files]');

      const setError = (field, message = '') => {
        const element = popup.querySelector(
          `[data-personalization-error="${field}"]`
        );
        element.textContent = message;
        element.hidden = !message;
      };

      const renderFiles = () => {
        filesList.innerHTML = '';
        selectedFiles.forEach((file, index) => {
          const item = document.createElement('li');
          item.className = 'giotto-personalization-file';

          const icon = document.createElement('span');
          icon.className = 'giotto-personalization-file__icon';
          icon.innerHTML = PERSONALIZATION_ICONS.paperclip;

          const copy = document.createElement('span');
          copy.className = 'giotto-personalization-file__copy';
          const name = document.createElement('strong');
          name.textContent = file.name;
          const size = document.createElement('span');
          size.textContent = formatFileSize(file.size);
          copy.append(name, size);

          const removeButton = document.createElement('button');
          removeButton.type = 'button';
          removeButton.className = 'giotto-personalization-file__remove';
          removeButton.dataset.personalizationRemoveFile = String(index);
          removeButton.setAttribute('aria-label', `Rimuovi ${file.name}`);
          removeButton.textContent = '×';

          item.append(icon, copy, removeButton);
          filesList.append(item);
        });
        uploadLabel.classList.toggle(
          'is-disabled',
          !selectedAction ||
            selectedFiles.length >= GIOTTO_PERSONALIZATION_MAX_FILES
        );
        fileInput.disabled =
          !selectedAction ||
          selectedFiles.length >= GIOTTO_PERSONALIZATION_MAX_FILES;
      };

      const getReferenceValue = (reference) =>
        `${reference.className}:${reference.id}`;

      const syncCardSelection = ({
        cards,
        dataAttribute,
        selectedReference: selected,
        select,
      }) => {
        const selectedValue = selected ? getReferenceValue(selected) : '';
        select.value = selectedValue;
        cards.querySelectorAll(`[${dataAttribute}]`).forEach((card) => {
          const isSelected = card.getAttribute(dataAttribute) === selectedValue;
          card.classList.toggle('is-selected', isSelected);
          card.setAttribute('aria-checked', String(isSelected));
        });
      };

      const syncReferenceSelection = () =>
        syncCardSelection({
          cards: referenceCards,
          dataAttribute: 'data-personalization-reference-card',
          selectedReference,
          select: referenceSelect,
        });

      const syncProjectSelection = () =>
        syncCardSelection({
          cards: projectCards,
          dataAttribute: 'data-personalization-project-card',
          selectedReference: selectedProjectReference,
          select: projectSelect,
        });

      const renderReferenceLoadingCards = (cards) => {
        cards.innerHTML = '';
        cards.hidden = false;
        for (let index = 0; index < 4; index += 1) {
          const skeleton = document.createElement('span');
          skeleton.className =
            'giotto-personalization-reference-card is-loading';
          skeleton.setAttribute('aria-hidden', 'true');
          skeleton.innerHTML = `
            <span class="giotto-personalization-reference-card__preview"></span>
            <span class="giotto-personalization-reference-card__copy">
              <span class="giotto-personalization-reference-card__skeleton-line"></span>
              <span class="giotto-personalization-reference-card__skeleton-line giotto-personalization-reference-card__skeleton-line--short"></span>
            </span>`;
          cards.append(skeleton);
        }
      };

      const renderReferenceCards = ({
        cards,
        dataAttribute,
        fallbackIcon,
        items,
        syncSelection,
      }) => {
        cards.innerHTML = '';
        cards.hidden = items.length === 0;

        items.forEach((reference) => {
          const card = document.createElement('button');
          card.type = 'button';
          card.className = 'giotto-personalization-reference-card';
          card.setAttribute(dataAttribute, getReferenceValue(reference));
          card.setAttribute('role', 'radio');
          card.setAttribute('aria-checked', 'false');
          card.setAttribute(
            'aria-label',
            [
              reference.name,
              reference.contextName,
              reference.isGlobal ? 'Predefinito' : 'Personalizzato',
            ]
              .filter(Boolean)
              .join(', ')
          );

          const preview = document.createElement('span');
          preview.className = 'giotto-personalization-reference-card__preview';
          const fallback = document.createElement('span');
          fallback.className =
            'giotto-personalization-reference-card__fallback';
          fallback.innerHTML = PERSONALIZATION_ICONS[fallbackIcon];

          if (reference.previewUrl) {
            const image = document.createElement('img');
            image.alt = '';
            image.loading = 'lazy';
            image.className = 'is-loading';
            image.addEventListener('load', () => {
              const ratio = image.naturalWidth / image.naturalHeight;
              preview.dataset.orientation =
                ratio > 1.08
                  ? 'horizontal'
                  : ratio < 0.92
                  ? 'vertical'
                  : 'square';
              image.classList.remove('is-loading');
              fallback.hidden = true;
            });
            image.addEventListener('error', () => {
              image.remove();
              fallback.hidden = false;
            });
            image.src = reference.previewUrl;
            preview.append(image);
          }
          preview.append(fallback);

          const copy = document.createElement('span');
          copy.className = 'giotto-personalization-reference-card__copy';
          const name = document.createElement('strong');
          name.textContent = reference.name;
          const metadata = document.createElement('span');
          metadata.textContent = [
            reference.contextName,
            reference.isGlobal ? 'Predefinito' : 'Personalizzato',
          ]
            .filter(Boolean)
            .join(' · ');
          copy.append(name, metadata);

          const check = document.createElement('span');
          check.className = 'giotto-personalization-reference-card__check';
          check.setAttribute('aria-hidden', 'true');
          check.textContent = '✓';

          card.append(preview, copy, check);
          cards.append(card);
        });
        syncSelection();
      };

      const renderProjectReferences = () => {
        projectSelect.innerHTML = '';
        const placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = 'Seleziona tipo di progetto';
        projectSelect.append(placeholder);

        projectReferences.forEach((reference) => {
          const option = document.createElement('option');
          option.value = getReferenceValue(reference);
          option.textContent = `${reference.name}${
            reference.isGlobal ? ' · predefinito' : ''
          }`;
          projectSelect.append(option);
        });

        projectSelect.value = selectedProjectReference
          ? getReferenceValue(selectedProjectReference)
          : '';
        projectSelect.disabled = projectReferences.length === 0;
        renderReferenceCards({
          cards: projectCards,
          dataAttribute: 'data-personalization-project-card',
          fallbackIcon: 'project_type',
          items: projectReferences,
          syncSelection: syncProjectSelection,
        });
        projectStatus.textContent =
          projectReferences.length === 0
            ? 'Nessun tipo di progetto disponibile.'
            : '';
        projectStatus.hidden = projectReferences.length > 0;
      };

      const renderReferences = () => {
        referenceSelect.innerHTML = '';
        const placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = `Seleziona ${personalizationType.label.toLowerCase()}`;
        referenceSelect.append(placeholder);

        references.forEach((reference) => {
          const option = document.createElement('option');
          option.value = getReferenceValue(reference);
          option.textContent = `${reference.name}${
            reference.isGlobal ? ' · predefinito' : ''
          }`;
          referenceSelect.append(option);
        });

        referenceSelect.value = selectedReference
          ? getReferenceValue(selectedReference)
          : '';
        referenceSelect.disabled = references.length === 0;
        referenceSearch.disabled =
          type !== 'template' || references.length === 0;
        const normalizedSearch = referenceSearchQuery
          .trim()
          .toLocaleLowerCase('it');
        const filteredReferences = normalizedSearch
          ? references.filter((reference) =>
              [reference.name, reference.contextName]
                .filter(Boolean)
                .some((value) =>
                  value.toLocaleLowerCase('it').includes(normalizedSearch)
                )
            )
          : references;
        // Il tetto vale solo dove ci sono le card e la ricerca per restringerle:
        // altrove la select elenca comunque tutti gli elementi.
        const visibleReferences =
          type === 'template'
            ? filteredReferences.slice(
                0,
                GIOTTO_PERSONALIZATION_REFERENCE_RENDER_LIMIT
              )
            : filteredReferences;
        if (type === 'template') {
          renderReferenceCards({
            cards: referenceCards,
            dataAttribute: 'data-personalization-reference-card',
            fallbackIcon: personalizationType.icon,
            items: visibleReferences,
            syncSelection: syncReferenceSelection,
          });
        } else {
          referenceCards.innerHTML = '';
          referenceCards.hidden = true;
          syncReferenceSelection();
        }
        if (references.length === 0) {
          referenceStatus.textContent = `Nessun ${personalizationType.label.toLowerCase()} disponibile.`;
        } else if (filteredReferences.length === 0) {
          referenceStatus.textContent =
            'Nessun elemento corrisponde alla ricerca.';
        } else if (
          type === 'template' &&
          filteredReferences.length >
            GIOTTO_PERSONALIZATION_REFERENCE_RENDER_LIMIT
        ) {
          referenceStatus.textContent = `Mostrati i primi ${GIOTTO_PERSONALIZATION_REFERENCE_RENDER_LIMIT} di ${filteredReferences.length} elementi. Usa la ricerca per restringere i risultati.`;
        } else {
          referenceStatus.textContent = '';
        }
        referenceStatus.hidden = !referenceStatus.textContent;
      };

      const loadProjectReferences = async () => {
        if (projectReferencesLoaded || projectReferencesLoading) return;
        projectReferencesLoading = true;
        projectSelect.disabled = true;
        projectCards.setAttribute('aria-busy', 'true');
        renderReferenceLoadingCards(projectCards);
        projectStatus.textContent = 'Caricamento in corso…';
        projectStatus.hidden = false;
        setError('project');

        try {
          projectReferences = await fetchGiottoPersonalizationReferences(
            'project_type'
          );
          projectReferencesLoaded = true;
          renderProjectReferences();
        } catch (error) {
          projectStatus.textContent =
            'Non è stato possibile caricare i tipi di progetto.';
          projectStatus.hidden = false;
          projectSelect.disabled = true;
          projectCards.innerHTML = '';
          projectCards.hidden = true;
        } finally {
          projectReferencesLoading = false;
          projectCards.setAttribute('aria-busy', 'false');
        }
      };

      const loadReferences = async () => {
        const requestKey = type;
        if (loadedReferenceKey === requestKey) return renderReferences();
        if (activeReferenceRequestKey === requestKey) return;
        activeReferenceRequestKey = requestKey;
        referenceSelect.disabled = true;
        referenceSearch.disabled = true;
        referenceCards.setAttribute('aria-busy', 'true');
        renderReferenceLoadingCards(referenceCards);
        referenceStatus.textContent = 'Caricamento in corso…';
        referenceStatus.hidden = false;
        setError('reference');

        try {
          const nextReferences = await fetchGiottoPersonalizationReferences(
            type
          );
          if (activeReferenceRequestKey !== requestKey) return;
          references =
            type === 'template'
              ? nextReferences.filter((reference) => !reference.isGlobal)
              : nextReferences;
          loadedReferenceKey = requestKey;
          renderReferences();
        } catch (error) {
          if (activeReferenceRequestKey !== requestKey) return;
          referenceStatus.textContent = `Non è stato possibile caricare ${personalizationType.label.toLowerCase()}.`;
          referenceStatus.hidden = false;
          referenceSelect.disabled = true;
          referenceSearch.disabled = true;
          referenceCards.innerHTML = '';
          referenceCards.hidden = true;
        } finally {
          referenceCards.setAttribute('aria-busy', 'false');
          if (activeReferenceRequestKey === requestKey) {
            activeReferenceRequestKey = null;
          }
        }
      };

      const updateAutomaticTitle = () => {
        if (!selectedAction) return;
        const nextAutomaticTitle =
          selectedAction === 'create'
            ? getNewPersonalizationTitle({
                orientation: selectedOrientation,
                projectTypeName: selectedProjectReference?.name,
                requestedName,
                type,
              })
            : selectedAction === 'update' && selectedReference
            ? getPersonalizationReferenceTitle(type, selectedReference.name)
            : getPersonalizationTicketTitle(type, selectedAction);
        if (!titleInput.value || titleInput.value === automaticTitle) {
          titleInput.value = nextAutomaticTitle;
        }
        automaticTitle = nextAutomaticTitle;
        titleCounter.textContent = `${titleInput.value.length}/${GIOTTO_PERSONALIZATION_TITLE_MAX_LENGTH}`;
      };

      const selectAction = (action) => {
        if (!personalizationActions[action]) return;
        selectedAction = action;

        popup
          .querySelectorAll('[data-giotto-personalization-action]')
          .forEach((button) => {
            const isSelected =
              button.dataset.giottoPersonalizationAction === action;
            button.classList.toggle('is-selected', isSelected);
            button.setAttribute('aria-checked', String(isSelected));
          });
        titleInput.disabled = false;
        descriptionInput.disabled = false;
        descriptionInput.placeholder = getPersonalizationDescriptionPlaceholder(
          type,
          action
        );
        summary.hidden = false;
        summaryValue.textContent = `${personalizationActions[action].label} — ${personalizationType.label}`;
        const requiresName = action === 'create';
        nameField.hidden = !requiresName;
        nameInput.disabled = !requiresName;
        const requiresProject =
          action === 'create' && ['format', 'template'].includes(type);
        projectField.hidden = !requiresProject;
        if (requiresProject) {
          loadProjectReferences();
        } else {
          selectedProjectReference = null;
          syncProjectSelection();
          setError('project');
        }
        const requiresOrientation = type === 'format' && action === 'create';
        orientationField.hidden = !requiresOrientation;
        orientationSelect.disabled = !requiresOrientation;
        if (!requiresOrientation) {
          selectedOrientation = '';
          orientationSelect.value = '';
          setError('orientation');
        }
        referenceField.hidden = action !== 'update';
        if (action === 'update') {
          loadReferences();
        } else {
          selectedReference = null;
          syncReferenceSelection();
          setError('reference');
        }
        updateAutomaticTitle();
        setError('action');
        renderFiles();
      };

      popup
        .querySelectorAll('[data-giotto-personalization-action]')
        .forEach((button) => {
          button.addEventListener('click', () =>
            selectAction(button.dataset.giottoPersonalizationAction)
          );
        });
      titleInput.addEventListener('input', () => {
        titleCounter.textContent = `${titleInput.value.length}/${GIOTTO_PERSONALIZATION_TITLE_MAX_LENGTH}`;
        setError('title');
      });
      nameInput.addEventListener('input', () => {
        requestedName = nameInput.value.trimStart();
        nameCounter.textContent = `${nameInput.value.length}/${GIOTTO_PERSONALIZATION_NAME_MAX_LENGTH}`;
        updateAutomaticTitle();
        setError('name');
        setError('title');
      });
      descriptionInput.addEventListener('input', () => {
        descriptionCounter.textContent = `${descriptionInput.value.length}/${GIOTTO_PERSONALIZATION_DESCRIPTION_MAX_LENGTH}`;
        setError('description');
      });
      projectSelect.addEventListener('change', () => {
        selectedProjectReference =
          projectReferences.find(
            (reference) => getReferenceValue(reference) === projectSelect.value
          ) || null;
        syncProjectSelection();
        updateAutomaticTitle();
        setError('project');
        setError('title');
      });
      orientationSelect.addEventListener('change', () => {
        selectedOrientation = orientationSelect.value;
        updateAutomaticTitle();
        setError('orientation');
        setError('title');
      });
      referenceSelect.addEventListener('change', () => {
        selectedReference =
          references.find(
            (reference) =>
              getReferenceValue(reference) === referenceSelect.value
          ) || null;
        syncReferenceSelection();
        updateAutomaticTitle();
        setError('reference');
        setError('title');
      });
      referenceSearch.addEventListener('input', () => {
        referenceSearchQuery = referenceSearch.value;
        renderReferences();
      });
      projectCards.addEventListener('click', (event) => {
        const card = event.target.closest(
          '[data-personalization-project-card]'
        );
        if (!card) return;
        selectedProjectReference =
          projectReferences.find(
            (reference) =>
              getReferenceValue(reference) ===
              card.dataset.personalizationProjectCard
          ) || null;
        syncProjectSelection();
        updateAutomaticTitle();
        setError('project');
        setError('title');
      });
      referenceCards.addEventListener('click', (event) => {
        const card = event.target.closest(
          '[data-personalization-reference-card]'
        );
        if (!card) return;
        selectedReference =
          references.find(
            (reference) =>
              getReferenceValue(reference) ===
              card.dataset.personalizationReferenceCard
          ) || null;
        syncReferenceSelection();
        updateAutomaticTitle();
        setError('reference');
        setError('title');
      });
      fileInput.addEventListener('change', () => {
        const nextFiles = Array.from(fileInput.files || []);
        if (
          selectedFiles.length + nextFiles.length >
          GIOTTO_PERSONALIZATION_MAX_FILES
        ) {
          setError(
            'files',
            `Puoi aggiungere al massimo ${GIOTTO_PERSONALIZATION_MAX_FILES} allegati.`
          );
          fileInput.value = '';
          return;
        }
        if (
          nextFiles.some(
            (file) =>
              file.size <= 0 ||
              file.size > GIOTTO_PERSONALIZATION_MAX_FILE_SIZE ||
              !hasAllowedGiottoPersonalizationExtension(file.name)
          )
        ) {
          setError(
            'files',
            'Controlla formato e dimensione degli allegati (massimo 15 MB).'
          );
          fileInput.value = '';
          return;
        }

        selectedFiles = [...selectedFiles, ...nextFiles];
        setError('files');
        fileInput.value = '';
        renderFiles();
      });
      filesList.addEventListener('click', (event) => {
        const removeButton = event.target.closest(
          '[data-personalization-remove-file]'
        );
        if (!removeButton) return;
        selectedFiles.splice(
          Number(removeButton.dataset.personalizationRemoveFile),
          1
        );
        setError('files');
        renderFiles();
      });

      if (selectedInitialAction) selectAction(selectedInitialAction);
    },
    preConfirm: async () => {
      const titleInput = popupElement.querySelector(
        '#giotto-personalization-title'
      );
      const descriptionInput = popupElement.querySelector(
        '#giotto-personalization-description'
      );
      const setError = (field, message = '') => {
        const element = popupElement.querySelector(
          `[data-personalization-error="${field}"]`
        );
        element.textContent = message;
        element.hidden = !message;
      };
      const title = titleInput.value.trim();
      const description = descriptionInput.value.trim();
      const normalizedRequestedName = requestedName.trim();
      let invalid = false;

      if (!selectedAction) {
        setError('action', 'Scegli il tipo di richiesta.');
        invalid = true;
      }
      if (!title) {
        setError('title', 'Inserisci il titolo del ticket.');
        invalid = true;
      }
      if (!description) {
        setError('description', 'Inserisci una descrizione.');
        invalid = true;
      }
      if (selectedAction === 'create' && !normalizedRequestedName) {
        setError(
          'name',
          `Inserisci il nome del nuovo ${personalizationType.label.toLowerCase()}.`
        );
        invalid = true;
      }
      if (
        selectedAction === 'create' &&
        ['format', 'template'].includes(type) &&
        !selectedProjectReference
      ) {
        setError('project', 'Seleziona il tipo di progetto.');
        invalid = true;
      }
      if (
        type === 'format' &&
        selectedAction === 'create' &&
        !selectedOrientation
      ) {
        setError('orientation', 'Seleziona l’orientamento.');
        invalid = true;
      }
      if (selectedAction === 'update' && !selectedReference) {
        setError(
          'reference',
          `Seleziona ${personalizationType.label.toLowerCase()} da modificare.`
        );
        invalid = true;
      }
      if (invalid) {
        Swal.showValidationMessage('Controlla i campi evidenziati.');
        return false;
      }

      try {
        await createGiottoPersonalization({
          action: selectedAction,
          description,
          files: selectedFiles,
          object: type,
          ...(selectedOrientation ? { orientation: selectedOrientation } : {}),
          ...(normalizedRequestedName && selectedAction === 'create'
            ? { requestedName: normalizedRequestedName }
            : {}),
          ...(selectedProjectReference
            ? {
                parentReference: getPersonalizationReferencePayload(
                  selectedProjectReference
                ),
              }
            : {}),
          ...(selectedReference
            ? {
                reference: getPersonalizationReferencePayload(
                  selectedReference
                ),
              }
            : {}),
          title,
        });
        return true;
      } catch (error) {
        Swal.showValidationMessage(getPersonalizationRequestError(error));
        return false;
      }
    },
  });

  if (result.isConfirmed) {
    Swal.fire({
      toast: true,
      position: 'top-end',
      icon: 'success',
      title: 'Richiesta inviata al Supporto',
      showConfirmButton: false,
      timer: 3500,
      timerProgressBar: true,
    });
    return 'submitted';
  }
  if (result.isDenied) return 'back';
  return 'cancelled';
};

let personalizationCatalogListenerRegistered = false;

export const openGiottoPersonalizationCatalog = async () => {
  let returnToCatalog = true;

  while (returnToCatalog) {
    const typeResult = await showPersonalizationChoiceDialog({
      title: 'Richiedi una personalizzazione Giotto',
      subtitle: 'Scegli il tipo di richiesta da inviare al Supporto.',
      choices: GIOTTO_PERSONALIZATION_TYPES,
    });

    if (!GIOTTO_PERSONALIZATION_TYPES[typeResult.value]) {
      return false;
    }

    const requestResult = await showGiottoPersonalizationRequestForm({
      type: typeResult.value,
      showBackButton: true,
    });

    if (requestResult === 'submitted') return true;
    returnToCatalog = requestResult === 'back';
  }

  return false;
};

export const openGiottoPersonalizationRequest = async (
  type,
  action = 'create'
) => {
  const personalizationActions = getPersonalizationActions(type);
  if (!GIOTTO_PERSONALIZATION_TYPES[type] || !personalizationActions[action]) {
    return false;
  }

  const result = await showGiottoPersonalizationRequestForm({
    initialAction: action,
    showBackButton: true,
    type,
  });
  if (result === 'back') {
    return openGiottoPersonalizationCatalog();
  }
  return result === 'submitted';
};

export const initGiottoPersonalizationRequests = () => {
  if (
    personalizationCatalogListenerRegistered ||
    typeof window === 'undefined'
  ) {
    return;
  }

  window.addEventListener(GLEAP_PERSONALIZATION_CATALOG_EVENT, () => {
    openGiottoPersonalizationCatalog();
  });
  personalizationCatalogListenerRegistered = true;
};

export const requestNewProject = async () => {
  return openGiottoPersonalizationRequest('project_type');
};

export const requestNewFormat = async () => {
  return openGiottoPersonalizationRequest('format');
};

export const requestNewInfoAf = async () => {
  const result = await Swal.fire({
    icon: 'info',
    title: 'Vuoi maggiori informazioni su AnnunciFunebri.it?',
    text:
      'Indicaci come e quando possiamo raggiungerti e invia il messaggio al nostro team una volta completato il testo',
    showCancelButton: true,
    confirmButtonText: '<span>Richiedi Informazioni</span>',
    cancelButtonText: 'Non ora',
    customClass: {
      confirmButton: 'requestNewInfoAf',

      confirmButtonColor: '#278e8e',
    },
  });

  if (result.isConfirmed) {
    triggerChatFlow('hs_g_annuncifunebri_getinfo');
  }
};

export const requestInfoEterno = async () => {
  const result = await Swal.fire({
    icon: 'info',
    title: 'Vuoi saperne di più su Eterno?',
    text:
      'Con Eterno puoi arricchire i tuoi annunci funebri con testi personalizzati, dettagli del rosario, streaming del funerale e molto altro. Vuoi ricevere maggiori informazioni?',
    showCancelButton: true,
    confirmButtonText: '<span>Sì, voglio saperne di più</span>',
    cancelButtonText: 'Non ora',
    customClass: {
      confirmButton: 'requestInfoEterno',
      confirmButtonColor: '#278e8e',
    },
  });

  if (result.isConfirmed) {
    triggerChatFlow(
      'Ciao! Sono interessato a saperne di più su Eterno per arricchire gli annunci funebri con testi personalizzati, rosario, streaming, ecc. Potete darmi maggiori informazioni?'
    );
  }
};

// Il catalogo personalizzazioni non copre gli eventi: la richiesta resta un
// flusso di supporto Gleap, altrimenti il bottone in sidebar non farebbe nulla.
export const requestNewEvent = async () => {
  const result = await Swal.fire({
    icon: 'info',
    title: 'Hai bisogno di un Evento Personalizzato?',
    text:
      'Se desideri aggiungere un evento personalizzato per questo progetto clicca su "Richiedi Evento" per indicarci quale evento vorresti creare. Invia il messaggio al nostro team una volta completato il testo',
    showCancelButton: true,
    confirmButtonText: '<span>Richiedi Evento</span>',
    cancelButtonText: 'Non adesso',
    customClass: {
      confirmButton: 'requestNewEvent',

      confirmButtonColor: '#278e8e',
    },
  });

  if (!result.isConfirmed) return false;

  triggerChatFlow('hs_g_new_project_event');
  return true;
};
export const requestNewTemplate = async () => {
  return openGiottoPersonalizationRequest('template');
};
